import { UIIcon } from '../config/icons';
import { ABEventList } from '../lib/ab/event';
import { ABScreen } from '../lib/ab/screen';
import { ABEventDateFilter, abGetDefaultCity, abGetRangeForDateFilter, makeCitySegmented, makeCitySelect, makeDateFilterExclusiveToggles, makeEventSortSegmented, makeEventSortSelect, makeGenreExclusiveToggles, makeGenreSelect, TLDateFilterSelect } from '../lib/ab/widgets-home';
import { aTrackPage } from '../lib/mdl/a-track';
import { cCityBySlug, CCityId } from '../lib/mdl/c-city';
import { chEventListActiveByCity, chEventListInRange, chEventListMatchesQuery, chEventListOnline, chEventListPast, chEventListPastByYear, CHEventListView } from '../lib/mdl/c-event-home';
import { c_event_tags, CEventTagId, cEventTagName } from '../lib/mdl/c-event-tag';
import { CProducerId } from '../lib/mdl/c-producer';
import { CVenueId } from '../lib/mdl/c-venue';
import { the_user } from '../lib/me/login';
import { getCountry } from '../lib/me/me';
import { UIButton } from '../lib/ui/component/button';
import { UIDialog } from '../lib/ui/component/dialog';
import { UISegmented } from '../lib/ui/component/segmented';
import { UIExclusiveToggles, UIToggleButton } from '../lib/ui/component/toggle';
import { $el } from '../lib/ui/elements';
import { UIValue } from '../lib/ui/types';
import { UIViewSpec } from '../lib/ui/view';
import { $on, YEventHandlers, YValueEvent } from '../lib/y/events';
import { $i18n } from '../lib/y/i18n';
import { yCopy } from '../lib/y/object';
import { $time, YTime } from '../lib/y/time';
import { HomeMenu } from './menu';

interface SearchQuery {
    text?: string;
    venue_id?: CVenueId;
    producer_id?: CProducerId;
    tag_id?: CEventTagId;
    date?: ABEventDateFilter;
    video?: boolean;
    film?: boolean;
    stream?: boolean;
    from?: number;
    to?: number;
}

interface SearchFilter {
    sort: 'date' | 'popular';
    category: 'event' | 'play';
    city: CCityId;
    date?: ABEventDateFilter;
    from?: YTime;
    to?: YTime;
    genre?: CEventTagId;
}

class FilterDialog extends UIDialog<SearchFilter> {
    $handlers: YEventHandlers<YValueEvent<SearchFilter>>;

    constructor(value: SearchFilter) {
        super({ close: true, classes: 'm-550 m-mobile-fs' });

        let from_value = value.from;
        let to_value = value.to;

        const sort = makeEventSortSegmented();
        const date = makeDateFilterExclusiveToggles(v => {
            [from_value, to_value] = v;
        });
        const genre = makeGenreExclusiveToggles(true);

        sort.value = value.sort;
        date.value = value.date || (value.from && value.to && 'range');
        genre.value = value.genre;

        const closeDialog = () => {
            value = yCopy(value);
            value.sort = sort.value || value.sort;
            value.date = date.value;
            if(value.date) {
                if(value.date == 'range') {
                    value.date = undefined;
                    value.from = from_value;
                    value.to = to_value;
                } else {
                    [value.from, value.to] = abGetRangeForDateFilter(value.date);
                }
            } else {
                value.from = undefined;
                value.to = undefined;
            }

            value.genre = genre.value;
            this.dismiss(value);
        };

        this.classList.add(value.category == 'event' ? 'ui-tint-midred' : 'ui-tint-darkred');

        this.content.ui_add($el.DIV('w-block l-form m-center', [
            value.category == 'event' && [
                $el.H3('heading', $i18n.tl.h_filter_date),
                date,
                $el.DIV('s-medium'),
            ],
            $el.H3('heading', $i18n.tl.h_filter_category),
            genre,
            $el.DIV('s-medium'),
            $el.H3('heading', $i18n.tl.h_filter_sort),
            sort,
            $el.DIV('s-medium'),
            $el.DIV('l-button-row', [
                new UIButton({
                    title: $i18n.tl.b_filter_clear, classes: 'ui-tint-reverse m-hollow', on_click: () => {
                        date.value = undefined;
                        genre.value = undefined;
                    }
                }),
                new UIButton({ title: $i18n.tl.b_filter_show, classes: 'ui-tint-reverse', on_click: closeDialog })
            ])
        ]));
    }
}

export class HomeSearchScreen extends ABScreen {
    constructor(private _query: SearchQuery) {
        super({ menu: new HomeMenu })
    }

    protected override fillContent(content: UIViewSpec): void {
        const query = this._query;

        let filter: SearchFilter = {
            category: query.video ? 'play' : 'event',
            city: the_user!.city_id,
            genre: query.tag_id,
            date: query.date,
            from: query.from ? $time(query.from) : undefined,
            to: query.to ? $time(query.to) : undefined,
            sort: !(query.date || query.from || query.to) ? 'popular' : 'date',
        }

        if(query.date)
            [filter.from, filter.to] = abGetRangeForDateFilter(query.date);

        const event_list = new ABEventList('events', 'list', 'event', [], { filter: 'active', from: query.from ? $time(query.from) : undefined });

        const category_values: UIValue<'play' | 'event'>[] = [{ key: 'event', name: $i18n.tl.events }, { key: 'play', name: 'Play' }];
        const category = new UISegmented(category_values, { kind: 'k-under' });
        const genre = makeGenreSelect();
        const date = new TLDateFilterSelect;
        const city = makeCitySelect();
        const sort = makeEventSortSelect();

        function updateWidgets() {
            category.value = filter.category;
            genre.value = filter.genre;
            city.value = filter.city;
            sort.value = filter.sort;
            date.value = [filter.date, filter.from, filter.to];
        }

        function updateList() {
            if(filter.category == 'play') {
                header.classList.add('f-play');
                date.ui_hide();
                city.ui_hide();
            } else {
                header.classList.remove('f-play');
                date.ui_show();
                city.ui_show();
            }

            Promise.all([chEventListActiveByCity(filter.city), chEventListOnline(getCountry())]).then(r => {
                let events = r[0].concat(r[1]);

                if(filter.category == 'play') {
                    events = events.filter(e => e.has_videos);

                    if(query.film === true)
                        events = events.filter(e => e.event_tag_ids.includes('01716444-dd98-70bf-8050-f538781c1a5a' as CEventTagId));
                    else if(query.film === false)
                        events = events.filter(e => !e.event_tag_ids.includes('01716444-dd98-70bf-8050-f538781c1a5a' as CEventTagId));
                } else {
                    events = events.filter(e => !e.has_videos);

                    if(filter.from && filter.to) {
                        event_list.from = filter.from;
                        events = events.filter(e => chEventListInRange(e, filter.from!, filter.to!))
                    } else {
                        event_list.from = undefined;
                    }
                }

                if(query.text) {
                    const words = query.text.split(' ');
                    events = events.filter(e => chEventListMatchesQuery(e, words));
                }

                if(query.venue_id)
                    events = events.filter(e => e.venue_id == query.venue_id);
                if(query.producer_id)
                    events = events.filter(e => e.producer_id == query.producer_id);

                const genre = filter.genre;
                if(genre)
                    events = events.filter(e => e.event_tag_ids.includes(genre));
                event_list.events = events;
                event_list.setMode('search', filter.sort);
            });
        }

        const header = $el.DIV('l-form m-center tl-search-header', [
            query.text && [$el.DIV('r-query', [$i18n.tl.h_search, $el.SPAN('r-text', ' ‘' + query.text + '’')]), $el.DIV('s-medium')],
            category,
            $el.DIV('tl-filter-bar', [
                $el.SPAN('r-title', $i18n.tl.h_filter),
                genre,
                date,
                city,
                $el.SPAN('r-title m-break', $i18n.tl.h_filter_sort),
                sort
            ]),
            new UIButton({
                icon: UIIcon.GEAR | UIIcon.WEIGHT_SOLID, classes: 'tl-filter-button ui-tint-reverse', on_click: () => {
                    new FilterDialog(filter).present().then(v => {
                        filter = v;
                        updateList();
                        updateWidgets();
                    });
                }
            })
        ]);

        content.ui_add([
            $el.SECTION('w-block', [
                header,
                $el.DIV('s-small'),
                event_list
            ])
        ]);

        updateList();
        updateWidgets();

        $on(category, 'value', v => {
            filter.category = v!;
            updateList();
        });

        $on(city, 'value', v => {
            filter.city = v!;
            updateList();
        });

        $on(date, 'value', v => {
            [filter.date, filter.from, filter.to] = v;
            updateList();
        });

        $on(genre, 'value', v => {
            filter.genre = v;
            updateList();
        });

        $on(sort, 'value', v => {
            filter.sort = v!;
            updateList();
        });
    }
}

export class HomePastScreen extends ABScreen {
    constructor(private _year?: string, private _tag?: CEventTagId, private _city?: string, private _genre?: string) {
        super({ menu: new HomeMenu });
    }

    protected override fillContent(content: UIViewSpec): void {
        const year = this._year;

        const list = new ABEventList('past', 'list', 'event', [], { filter: 'past', sort: 'popular' });
        const days_values = [{ key: 90, title: $i18n.tl.o_past_3 }, { key: 365, title: $i18n.tl.o_past_all }];
        const days = new UIExclusiveToggles(days_values, t => new UIToggleButton(t), { classes: 'ui-toggle-buttons' });
        days.value = 90;
        const city = makeCitySegmented({ kind: 'k-under' });
        city.value = (this._city && cCityBySlug(this._city)?.id) || abGetDefaultCity().id;
        const tag_values = c_event_tags.all.filter(t => t.is_genre).slice(0, 9).map(t => ({ key: t.id, title: cEventTagName(t) })).sort((a, b) => a.title.localeCompare(b.title));

        if(this._genre && !this._tag) {
            for(const tag of c_event_tags.all) {
                if(tag.is_genre && tag.slug == this._genre)
                    this._tag = tag.id;
            }
        }

        if(this._tag && !tag_values.find(t => t.key == this._tag)) {
            const tag = c_event_tags.maybe(this._tag);
            if(tag)
                tag_values.push({ key: tag.id, title: cEventTagName(tag) });
        }

        tag_values.unshift({ key: '' as CEventTagId, title: $i18n.tl.o_tag_all });
        const tags = new UIExclusiveToggles(tag_values, t => new UIToggleButton(t), { classes: 'ui-toggle-buttons' });

        $on(tags, 'value', tag => {
            list.custom_filter = tag ? ((e: CHEventListView) => !!e.event_tag_ids.includes(tag)) : undefined;
        });

        tags.value = (this._tag || '') as CEventTagId;

        content.ui_add($el.SECTION('w-block', [
            $el.DIV('l-form m-center', [
                $el.H1('bigtitle', $i18n.tl.h_past),
                $el.DIV('s-small'),
                !year && days,
                city,
                tags
            ]),
            $el.DIV('s-small'),
            list
        ]));

        const fillEvents = () => {
            const d = days.value;
            const c = city.value;

            if(year) {
                chEventListPastByYear(c!, year).then(events => {
                    // TODO: find a way to automate this check with some kind of token thingy
                    if(d != days.value || c != city.value) return;
                    list.events = events;
                });
            } else {
                chEventListPast(c!, d!).then(events => {
                    // TODO: find a way to automate this check with some kind of token thingy
                    if(d != days.value || c != city.value) return;
                    list.events = events;
                });
            }
        }

        $on(city, 'value', fillEvents);
        $on(days, 'value', fillEvents);

        fillEvents();

        aTrackPage('past_events');
    }
}
